import { Progress } from '@backstage/core-components';
import {
  DevHubAlert,
  DevHubAvatar,
  DevHubBox,
  DevHubGrid,
  DevHubLink,
  DevHubPaper,
  DevHubTypography,
} from '@devxdiscover/devhub-ui';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import {
  areThereSecVulns,
  UserData,
  useSecurityVulns,
} from '../../services/secVulns';

/**
 * The properties for the {@link SecurityVulnerabilities} component.
 */
type secVulnsProps = {
  expanded: boolean;
  onDataLoaded: (hasData: boolean) => void;
};

/**
 * A component that displays a summary of security vulnerabilities for authenticated user.
 * @param props - The properties for the component.
 */
export const SecurityVulnerabilities = (props: secVulnsProps) => {
  const {
    value: secVulnsValue,
    loading: secVulnsIsLoading,
    error: secVulnsHasError,
  } = useSecurityVulns();

  const [securityVulns, setSecurityVulns] = useState<UserData>({
    data: {
      getUser: {
        login: '',
        secvulnSummaryByLogin: {
          login: '',
          sp1: '',
          sp2: '',
          sp3: '',
          sp4: '',
        },
        secvulnSummaryDetailsByLogin: [],
      },
    },
    errors: [],
  });

  useEffect(() => {
    if (secVulnsValue !== undefined) {
      setSecurityVulns(secVulnsValue);
    }
  }, [secVulnsValue]);

  useEffect(() => {
    props.onDataLoaded(
      securityVulns?.data?.getUser?.secvulnSummaryDetailsByLogin.length > 0,
    );
  });

  if (secVulnsHasError) {
    return (
      <DevHubAlert severity="error">
        Error fetching security vulnerabilities
      </DevHubAlert>
    );
  }

  const statusColors: { [key: string]: any } = {
    6: 'orange',
    5: 'green',
    4: 'purple',
    3: 'blue',
    2: 'yellow',
    1: 'red',
  };

  return (
    <>
      <DevHubTypography variant="subtitle2">
        Security Vulnerabilities
      </DevHubTypography>
      {!secVulnsIsLoading &&
        (securityVulns?.errors?.length > 0 || secVulnsHasError) && (
          <DevHubTypography>
            There was an error retrieving your user data.
          </DevHubTypography>
        )}
      {!secVulnsIsLoading &&
        !(securityVulns?.errors?.length > 0) &&
        !areThereSecVulns(securityVulns) && (
          <DevHubTypography>
            There are no security vulnerabilities associated with your user
            data.
          </DevHubTypography>
        )}
      {secVulnsIsLoading && <Progress />}

      {areThereSecVulns(securityVulns) && props.expanded && (
        <>
          <DevHubBox
            display="flex"
            justifyContent="spaceBetween"
            flexWrap="wrap"
            flexGrow={1}
          >
            {securityVulns?.data?.getUser?.secvulnSummaryDetailsByLogin?.map(
              object => (
                <DevHubPaper
                  key={
                    object?.vulnerabilityId
                      ? object?.vulnerabilityId
                      : object?.vulnerabilityLink
                  }
                  elevation={0}
                >
                  <DevHubBox order={2}>
                    {object?.vulnerabilityId && (
                      <DevHubPaper elevation={0}>
                        <DevHubTypography>
                          {' '}
                          {object?.securityPriority}{' '}
                        </DevHubTypography>
                        <DevHubLink
                          href={object?.vulnerabilityLink}
                          target="_blank"
                          rel="noreferrer"
                          underline="hover"
                          variant="subtitle2"
                        >
                          {object?.vulnerabilityId}
                        </DevHubLink>
                      </DevHubPaper>
                    )}
                    {object?.status && (
                      <DevHubPaper elevation={0}>
                        <DevHubTypography>{object?.status}</DevHubTypography>
                      </DevHubPaper>
                    )}
                  </DevHubBox>
                </DevHubPaper>
              ),
            )}
          </DevHubBox>
        </>
      )}

      {areThereSecVulns(securityVulns) &&
        !secVulnsIsLoading &&
        !props.expanded && (
          <>
            <DevHubTypography variant="subtitle2">
              <DevHubGrid container>
                <DevHubGrid item xs>
                  <DevHubBox display="flex">
                    <DevHubBox order={1}>
                      <DevHubAvatar
                        className={clsx(
                          parseInt(
                            securityVulns?.data?.getUser?.secvulnSummaryByLogin
                              ?.sp1,
                            10,
                          ) > 0
                            ? statusColors[1]
                            : statusColors[5],
                        )}
                      >
                        {
                          securityVulns?.data?.getUser?.secvulnSummaryByLogin
                            ?.sp1
                        }
                      </DevHubAvatar>
                    </DevHubBox>
                    <DevHubBox order={2}>
                      <DevHubPaper elevation={0}>
                        <DevHubTypography>sp1 vulnerabilities</DevHubTypography>
                      </DevHubPaper>
                    </DevHubBox>
                  </DevHubBox>
                </DevHubGrid>
                <DevHubGrid item xs>
                  <DevHubBox display="flex">
                    <DevHubBox order={3}>
                      <DevHubAvatar
                        className={clsx(
                          parseInt(
                            securityVulns?.data?.getUser?.secvulnSummaryByLogin
                              ?.sp2,
                            10,
                          ) > 0
                            ? statusColors[6]
                            : statusColors[5],
                        )}
                      >
                        {
                          securityVulns?.data?.getUser?.secvulnSummaryByLogin
                            ?.sp2
                        }
                      </DevHubAvatar>
                    </DevHubBox>
                    <DevHubBox order={4}>
                      <DevHubPaper elevation={0}>
                        <DevHubTypography>sp2 vulnerabilities</DevHubTypography>
                      </DevHubPaper>
                    </DevHubBox>
                  </DevHubBox>
                </DevHubGrid>
              </DevHubGrid>
              <DevHubGrid container>
                <DevHubGrid item xs>
                  <DevHubBox display="flex">
                    <DevHubBox order={1}>
                      <DevHubAvatar
                        className={clsx(
                          parseInt(
                            securityVulns?.data?.getUser?.secvulnSummaryByLogin
                              ?.sp3,
                            10,
                          ) > 0
                            ? statusColors[2]
                            : statusColors[5],
                        )}
                      >
                        {
                          securityVulns?.data?.getUser?.secvulnSummaryByLogin
                            ?.sp3
                        }
                      </DevHubAvatar>
                    </DevHubBox>
                    <DevHubBox order={2}>
                      <DevHubPaper elevation={0}>
                        <DevHubTypography>sp3 vulnerabilities</DevHubTypography>
                      </DevHubPaper>
                    </DevHubBox>
                  </DevHubBox>
                </DevHubGrid>
                <DevHubGrid item xs>
                  <DevHubBox display="flex">
                    <DevHubBox order={3}>
                      <DevHubAvatar
                        className={clsx(
                          parseInt(
                            securityVulns?.data?.getUser?.secvulnSummaryByLogin
                              ?.sp4,
                            10,
                          ) > 0
                            ? statusColors[3]
                            : statusColors[5],
                        )}
                      >
                        {
                          securityVulns?.data?.getUser?.secvulnSummaryByLogin
                            ?.sp4
                        }
                      </DevHubAvatar>
                    </DevHubBox>
                    <DevHubBox order={4}>
                      <DevHubPaper elevation={0}>
                        <DevHubTypography>sp4 vulnerabilities</DevHubTypography>
                      </DevHubPaper>
                    </DevHubBox>
                  </DevHubBox>
                </DevHubGrid>
              </DevHubGrid>
            </DevHubTypography>
          </>
        )}
    </>
  );
};
